import isEmpty from 'lodash/isEmpty';

import { Tab, Tabs as DsTabs } from '@cast/design-system';
import { usePath } from '@cast/utils';

import { Link } from 'components/router';
import { ComingSoonTab } from 'components/tabs/ComingSoonTab';
import {
  makeCostMonitoringComputeCostLink,
  makeCostMonitoringEfficiencyLink,
  makeCostMonitoringGpuUtilizationLink,
  makeCostMonitoringNetworkCostLink,
  makeCostMonitoringReliabilityLink,
} from 'features/cluster/cost-report/links';
import {
  makeOrgCostMonitoringComputeCostLink,
  makeOrgCostMonitoringEfficiencyLink,
  makeOrgCostMonitoringGpuUtilizationLink,
  makeOrgCostMonitoringNetworkCostLink,
} from 'features/organization/cost-report/links';

import { ReportTypeSegmentKeys } from '../../cluster/cost-report/navigation';
import { useCostReportContext } from '../hooks/useCostReportContext';
import { CostReportTab } from '../types/common';

import { useCluster } from '@cast/shared-cluster-context-data-access';

export type CostReportTabsProps = {
  enabledTabs: CostReportTab[];
  currentTab?: CostReportTab;
  tabs?: Partial<Record<CostReportTab, TabValue>>;
};

type ReportTypeTabValue = {
  label: string;
  reportType: ReportTypeSegmentKeys;
};

type LinkTabValue = {
  label: string;
  link: string;
};

type LabelOnlyTabValue = {
  label: string;
};

export type TabValue = ReportTypeTabValue | LinkTabValue | LabelOnlyTabValue;

const hasReportType = (tab: TabValue | undefined): tab is ReportTypeTabValue =>
  !!tab && 'reportType' in tab;

export const defaultTabs: Partial<Record<CostReportTab, TabValue>> = {
  [CostReportTab.COMPUTE_COST]: {
    label: 'Compute cost',
    reportType: 'computeCost',
  },
  [CostReportTab.RELIABILITY]: {
    label: 'Reliability',
    reportType: 'reliability',
  },
  [CostReportTab.NETWORK_COST]: {
    label: 'Network cost',
    reportType: 'networkCost',
  },
  [CostReportTab.EFFICIENCY]: { label: 'Efficiency', reportType: 'efficiency' },
  [CostReportTab.TOTAL_COST]: { label: 'Total cost' },
};

export const CostReportTabs = ({
  enabledTabs,
  currentTab,
  tabs = defaultTabs,
}: CostReportTabsProps) => {
  const { cluster } = useCluster();
  const isClusterLevel = !!cluster.id;

  const { routeProps } = useCostReportContext();

  const computeCostLink = usePath(
    isClusterLevel
      ? () => makeCostMonitoringComputeCostLink({ clusterId: cluster.id })
      : makeOrgCostMonitoringComputeCostLink,
    {
      enabled: !isEmpty(routeProps) && hasReportType(tabs?.COMPUTE_COST),
    }
  );

  const networkCostLink = usePath(
    isClusterLevel
      ? () => makeCostMonitoringNetworkCostLink({ clusterId: cluster.id })
      : makeOrgCostMonitoringNetworkCostLink,
    {
      enabled: !isEmpty(routeProps) && hasReportType(tabs?.NETWORK_COST),
    }
  );

  const efficiencyLink = usePath(
    isClusterLevel
      ? () => makeCostMonitoringEfficiencyLink({ clusterId: cluster.id })
      : makeOrgCostMonitoringEfficiencyLink,
    {
      enabled: !isEmpty(routeProps) && hasReportType(tabs?.EFFICIENCY),
    }
  );

  const gpuUtilizationLink = usePath(
    isClusterLevel
      ? () => makeCostMonitoringGpuUtilizationLink({ clusterId: cluster.id })
      : makeOrgCostMonitoringGpuUtilizationLink,
    {
      enabled: !isEmpty(routeProps) && hasReportType(tabs?.GPU_UTILIZATION),
    }
  );

  const reliabilityLink = usePath(
    () => makeCostMonitoringReliabilityLink({ clusterId: cluster.id }),
    {
      enabled:
        isClusterLevel &&
        !isEmpty(routeProps) &&
        hasReportType(tabs?.RELIABILITY),
    }
  );

  const makeLinkToReport = (reportType?: ReportTypeSegmentKeys) => {
    switch (reportType) {
      case 'computeCost': {
        return computeCostLink;
      }
      case 'networkCost': {
        return networkCostLink;
      }
      case 'efficiency': {
        return efficiencyLink;
      }
      case 'gpuUtilization': {
        return gpuUtilizationLink;
      }
      case 'reliability': {
        return reliabilityLink;
      }
      default: {
        return '/404';
      }
    }
  };

  if (isEmpty(routeProps)) {
    return null;
  }

  return (
    <DsTabs value={currentTab} size="medium" fullWidth>
      {(Object.entries(tabs) as Array<[CostReportTab, TabValue]>).map(
        ([value, tab]) => {
          const isEnabled = enabledTabs.includes(value);

          if (!isEnabled) {
            return <ComingSoonTab key={value} label={tab.label} />;
          }

          const link = hasReportType(tab)
            ? makeLinkToReport(tab.reportType)
            : 'link' in tab
            ? tab.link
            : undefined;

          if (link) {
            return (
              <Tab
                key={value}
                value={value}
                label={tab.label}
                component={Link}
                to={link}
              />
            );
          }

          return <Tab key={value} value={value} label={tab.label} />;
        }
      )}
    </DsTabs>
  );
};
